import React, { useEffect, useState, useRef, type ReactNode } from 'react'
import { useQueryParams } from '@fs/zion-router'
import { appPath } from '@fs/zion-config'
import axios from '@fs/zion-axios'
import { H3, MarginBox, Subheading } from '@fs/zion-ui'
import { i18n } from '@fs/zion-locale'
import { usePlaceReportsPageData } from '../PlaceReportsProvider'

const emptyLocales: string[] = []

function extractGuidelinesLocales(reports: string[] = []): string[] {
  const regex = /guidelines_(\w{2})\.md/
  return reports
    .map((report) => {
      const match = regex.exec(report)
      return match ? match[1] : null
    })
    .filter(Boolean) as string[]
}

function extractRegionSummaryLocales(reports: string[] = []): string[] {
  const regex = /region_summary_(\w{2})\.md/
  return reports
    .map((report) => {
      const match = regex.exec(report)
      return match ? match[1] : null
    })
    .filter(Boolean) as string[]
}

function extractScorecardLocales(reports: string[] = []): string[] {
  const regex = /score_breakdown_(\w{2})\.md/
  return reports
    .map((report) => {
      const match = regex.exec(report)
      return match ? match[1] : null
    })
    .filter(Boolean) as string[]
}

function getMarkdownPathFromArgs(
  locale: string,
  region?: string,
  country?: string,
  division?: string,
  fileType: 'guidelines' | 'scorecard' = 'guidelines'
): string | null {
  const filePrefix = fileType === 'scorecard' ? 'score_breakdown' : 'guidelines'

  if (region && country && division) {
    return `regions/${region}/countries/${country}/divisions/${division}/${filePrefix}_${locale}.md`
  }
  if (region && country) {
    return `regions/${region}/countries/${country}/${filePrefix}_${locale}.md`
  }

  if (region) {
    return `regions/${region}/region_summary_${locale}.md`
  }
  return null
}

type UseMarkdownContentResult = {
  markdown: string | null
  error: ReactNode | null
  loading: boolean
  region: string | undefined
  locales: string[]
  selectedLocale: string
  setSelectedLocale: (locale: string) => void
  fetchMarkdown: (markdownPath: string) => Promise<void>
  getMarkdownPath: (locale: string) => string | null
  hasScorecardAvailable: boolean
}

export default function useMarkdownContent(): UseMarkdownContentResult {
  const [markdown, setMarkdown] = useState<string | null>('')
  const [error, setError] = useState<ReactNode | null>(null)
  const [loading, setLoading] = useState(false)
  const { query } = useQueryParams()
  const { region, country, division } = query
  const {
    getRegion,
    getCountryAndRegion,
    getDivision,
    selectedLocale,
    setSelectedLocale,
    viewType,
    hasScorecardAvailable,
  } = usePlaceReportsPageData()
  const [locales, setLocales] = useState(emptyLocales)
  const isFetching = useRef(false)

  function getMarkdownPath(locale: string): string | null {
    return getMarkdownPathFromArgs(locale, region, country, division, viewType)
  }

  async function fetchMarkdown(markdownPath: string): Promise<void> {
    setLoading(true)
    setError(null)
    try {
      const url = `${appPath}/place-reports/markdown-content/?filename=${markdownPath}`
      const response = await axios.get<{ content: string }>(url)
      if (response.status !== 200) {
        throw new Error(`Network response was not ok ${response.status} - ${response.statusText} - ${markdownPath}`)
      }
      setMarkdown(response.data.content)
    } catch (err) {
      const error = err as Error
      setError(
        <MarginBox marginX="nano" marginY="nano">
          <H3 size="H5">
            {`File Missing - ${markdownPath}`}
            <Subheading>{error.message}</Subheading>
          </H3>
        </MarginBox>
      )
      setMarkdown(null)
    } finally {
      setLoading(false)
      isFetching.current = false
    }
  }

  useEffect(() => {
    function getNewSelectedLocale(extractedLocales: string[] = []): string {
      const i18nLanguageOrFalse = extractedLocales.includes(i18n.language) ? i18n.language : false
      const firstLocaleOrFalse = extractedLocales[0] || false
      return i18nLanguageOrFalse || firstLocaleOrFalse || 'en'
    }

    let reports: string[] = []
    let effectiveViewType: 'guidelines' | 'scorecard' = viewType

    // Get reports array for current place
    if (region && country && division) {
      reports = getDivision(division)?.division?.reports || []
    } else if (region && country) {
      reports = getCountryAndRegion(country)?.country?.reports || []
    } else if (region) {
      reports = getRegion(region)?.reports || []
      effectiveViewType = 'guidelines' // force guidelines for region summaries
    }

    // Extract locales based on effective view type
    let extractedLocales: string[]
    if (effectiveViewType === 'scorecard') {
      extractedLocales = extractScorecardLocales(reports)
    } else if (region && !country) {
      extractedLocales = extractRegionSummaryLocales(reports)
    } else {
      extractedLocales = extractGuidelinesLocales(reports)
    }

    const newSelectedLocale = getNewSelectedLocale(extractedLocales)
    setLocales(extractedLocales)
    setSelectedLocale(newSelectedLocale)

    const markdownPath = getMarkdownPathFromArgs(newSelectedLocale, region, country, division, effectiveViewType)
    if (markdownPath) {
      fetchMarkdown(markdownPath)
    } else {
      setMarkdown(null)
      setError(null)
    }
  }, [
    region,
    country,
    division,
    viewType,
    getRegion,
    getCountryAndRegion,
    getDivision,
    setSelectedLocale,
    hasScorecardAvailable,
  ])

  return {
    markdown,
    error,
    loading,
    region,
    locales,
    selectedLocale,
    setSelectedLocale,
    fetchMarkdown,
    getMarkdownPath,
    hasScorecardAvailable,
  }
}
